import { useSyncExternalStore } from 'react';

export type ThemePref = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

const THEME_KEY = 'certexam-theme';
const DEFAULT_PREF: ThemePref = 'light';

export function getThemePref(): ThemePref {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_KEY) : null;
  return v === 'dark' || v === 'light' || v === 'system' ? v : DEFAULT_PREF;
}

export function resolveTheme(pref: ThemePref = getThemePref()): ResolvedTheme {
  if (pref === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return pref;
}

/** Apply the resolved theme to <html> (class + color-scheme). */
export function applyTheme(pref: ThemePref = getThemePref()): void {
  const resolved = resolveTheme(pref);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.classList.toggle('light', resolved === 'light');
  root.style.colorScheme = resolved;
}

/** Persist + apply a new preference and notify subscribers. */
export function setThemePref(pref: ThemePref): void {
  localStorage.setItem(THEME_KEY, pref);
  applyTheme(pref);
  window.dispatchEvent(new Event('themechange'));
}

/** Call once at startup: applies the stored theme and keeps `system` in sync. */
export function initTheme(): void {
  applyTheme();
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (getThemePref() === 'system') {
      applyTheme('system');
      window.dispatchEvent(new Event('themechange'));
    }
  });
}

/** React hook returning the current resolved theme ('dark' | 'light'). */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('themechange', cb);
      return () => window.removeEventListener('themechange', cb);
    },
    () => resolveTheme(),
    () => 'light',
  );
}
